import { AlertCircle, RotateCcw, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { CircularProgress } from '@heidi/design-system/circular-progress';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';

import type { ChatUploadFileStatus } from '@/hooks/useChatFileUpload';
import { ImagePreviewModal } from './ImagePreviewModal';

interface ImageFileCardProps {
  /**
   * The image file to display
   */
  file: File;
  /**
   * Callback when the delete button is clicked
   */
  onDelete?: () => void;
  /**
   * Callback when the retry button is clicked (shown on error)
   */
  onRetry?: () => void;
  /**
   * Upload status for showing loading state
   */
  uploadStatus?: ChatUploadFileStatus;
  /**
   * Upload progress (0-100) for the loading indicator
   */
  uploadProgress?: number;
  /**
   * Presigned S3 URL to use once upload is complete
   */
  previewUrl?: string;
  /**
   * Error message to display in tooltip when upload fails
   */
  errorMessage?: string;
}

/**
 * A simple image thumbnail card with a delete button and upload progress indicator.
 * Clicking the image opens a full-size modal.
 * Styled to match LinkedContextBadge (patient/session attachments).
 */
export function ImageFileCard({
  file,
  onDelete,
  onRetry,
  uploadStatus,
  uploadProgress = 0,
  previewUrl,
  errorMessage,
}: ImageFileCardProps) {
  const intl = useIntl();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);

  // Read the file as a data URL (fallback when previewUrl is not available)
  useEffect(() => {
    // If we have a previewUrl and upload is complete, skip reading the file
    if (previewUrl && uploadStatus === 'completed') {
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setImageDataUrl(e.target.result as string);
      }
    };
    reader.readAsDataURL(file);
  }, [file, previewUrl, uploadStatus]);

  // Use previewUrl when available and upload is complete, otherwise use local data URL
  const displayImageSrc =
    uploadStatus === 'completed' && previewUrl ? previewUrl : imageDataUrl;

  const isLoading = uploadStatus === 'uploading' || uploadStatus === 'pending';
  const hasError = uploadStatus === 'error';

  if (!displayImageSrc && !isLoading) return null;

  return (
    <>
      {/* Container - relative for positioning the delete button and progress, group for hover */}
      <div className="group relative inline-block">
        {/* Image thumbnail - clickable when not loading */}
        <button
          type="button"
          onClick={() => !isLoading && setIsModalOpen(true)}
          disabled={isLoading}
          className={cn(
            'relative block size-14 overflow-hidden rounded-lg border bg-surface shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring-selected',
            isLoading
              ? 'cursor-default border-outline'
              : 'cursor-pointer border-outline hover:border-outline-hover',
            hasError && 'border-validation-destructive'
          )}
          aria-label={
            isLoading ? `Uploading ${file.name}` : `View ${file.name} full size`
          }
        >
          {/* Image or loading placeholder */}
          {displayImageSrc ? (
            <img
              src={displayImageSrc}
              alt={file.name}
              className={cn('size-full object-cover', isLoading && 'opacity-50')}
            />
          ) : (
            <div className="size-full bg-background-tertiary" />
          )}

          {/* Loading overlay with progress circle */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <CircularProgress
                progress={uploadProgress}
                size="lg"
                variant="overlay"
                aria-label={`Uploading ${Math.round(uploadProgress)}%`}
              />
            </div>
          )}

          {/* Error overlay with error icon */}
          {hasError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex size-6 items-center justify-center rounded-full bg-validation-destructive">
                      <AlertCircle
                        className="size-4 text-white"
                        aria-label="Upload failed"
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {errorMessage || 'Upload failed'}
                    <TooltipArrow />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </button>

        {/* Retry button - bottom right corner, shown on error */}
        {hasError && onRetry && (
          <Button
            iconOnly
            className="absolute bottom-0 right-0 size-5 min-w-5 translate-x-1/3 translate-y-1/3 rounded-full p-0.5"
            size="xxs"
            onClick={(e) => {
              e.stopPropagation();
              onRetry();
            }}
            aria-label={intl.formatMessage(
              {
                defaultMessage: 'Retry upload of {fileName}',
                id: 'KrL9xvOV70',
                description:
                  'Accessible label for the button that retries uploading one failed file',
              },
              { fileName: file.name }
            )}
            variant="secondary"
          >
            <RotateCcw className="size-3" />
          </Button>
        )}

        {/* Delete button - top right corner, hidden until hover (always visible during upload or error) */}
        {onDelete && (
          <Button
            iconOnly
            className={cn(
              'absolute right-0 top-0 size-5 min-w-5 -translate-y-1/3 translate-x-1/3 rounded-full p-0.5',
              'hover:opacity-100 focus:opacity-100 group-hover:opacity-100',
              // Always show during loading or error so user can cancel/delete
              isLoading || hasError ? 'opacity-100' : 'opacity-0'
            )}
            size="xxs"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            aria-label={
              isLoading ? `Cancel upload of ${file.name}` : `Remove ${file.name}`
            }
            variant="destructive"
          >
            <X />
          </Button>
        )}
      </div>

      {/* Full-size preview modal - only when not loading */}
      {displayImageSrc && !isLoading && (
        <ImagePreviewModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          imageSrc={displayImageSrc}
          imageAlt={file.name}
        />
      )}
    </>
  );
}
